import { useEffect, useMemo } from 'react';
import { AvailabilityEnum } from '../../../products/ProductTypes';
import { useOrderContext } from '../../contexts/Order.context';
import { IGridContextData, UseGridProps } from '../interfaces/use-grid.interface';
import { calculateProductTotalParts, calculateTotalOrderProduct } from '../orderProducts.utils';
import { OrderProductForGridContainerTransformer } from '../product/grid/components/orderProductGrid/components/grid/types/orderProductGridContainer.types';
import { useProductGrid } from '../product/grid/components/orderProductGrid/hooks/useProductGrid/useProductGrid';
import { useProductGridActions } from '../product/grid/hooks/useProductGridActions';
import { useGridStoreApiOptional } from '../product/grid/store/GridStoreProvider';
import { buildCombinationKey } from '../product/grid/store/gridStore.utils';
import useCopyPasteGrid from './useCopyPasteGrid';

export const useGrid = ({ product, orderId }: UseGridProps) => {
  const { copiedGrid, order } = useOrderContext();
  const gridStoreApi = useGridStoreApiOptional();
  const combinationKey = buildCombinationKey({
    productColorId: product.productColorId,
    availability: product.availability as AvailabilityEnum,
    deliveryWindowId: product.deliveryWindow?.id,
    orderId,
  });

  const {
    grid,
    hasValidPacks,
    suggestedGrid,
    suggestedPacks,
    selectedPacks,
    isLoading: isLoadingGrid,
    isFetching: isFetchingGrid,
    showSuggestedGrid,
    suggestionAchieved,
    resetSelectedPacks,
  } = useProductGrid({
    productColorId: product.productColorId,
    availability: product.availability as AvailabilityEnum,
    deliveryWindowId: product.deliveryWindow?.id,
    orderId,
    orderStatus: order?.status,
    kitId: product.kit?.id,
  });

  useEffect(() => {
    if (gridStoreApi) {
      gridStoreApi.getState().seed({ combinationKey, grid });
    }
  }, [gridStoreApi, combinationKey, grid]);

  const productForGrid = useMemo(
    () => ({ ...OrderProductForGridContainerTransformer.fromOrder(product), combinationKey }),
    [product, combinationKey],
  );

  const { onClickPaste } = useCopyPasteGrid({
    product: productForGrid,
    copiedGrid,
    grid,
    orderId,
    resetSelectedPacks,
  });

  const { handleSelectSuggestion } = useProductGridActions({
    grid,
    availability: product.availability as AvailabilityEnum,
    deliveryWindowId: product.deliveryWindow?.id,
    orderId,
    productColorId: product.productColorId,
  });

  const partsQuantity = calculateProductTotalParts(grid);
  const total = calculateTotalOrderProduct(grid);

  const value: IGridContextData = {
    isLoadingGrid,
    isFetchingGrid,
    combinationKey,
    handleClickPaste: onClickPaste,
    handleSelectSuggestion,
    grid,
    suggestedGrid,
    suggestedPacks,
    selectedPacks,
    hasValidPacks,
    partsQuantity,
    total,
    showSuggestedGrid,
    suggestionAchieved,
    resetSelectedPacks,
  };

  return value;
};
